import React from "react";
import { API } from "../constants";

/**
 * Admin-only Manuals card on /admin.
 * Each manual box is a single large hyperlink — click anywhere on the box to
 * view the PDF in a new tab. A small download icon in the corner triggers the
 * download variant (uses stopPropagation so it doesn't double-fire).
 *
 * No access code is required because the user is already authenticated as
 * administrator (they passed 504to713 to reach /admin). The admin password is
 * silently appended to the administrator-manual URL behind the scenes.
 */
export default function AdminManualsCard({ adminPassword }) {
  const subView = `${API}/manuals/subscriber`;
  const subDl = `${API}/manuals/subscriber?download=true`;
  const k = encodeURIComponent(adminPassword || "");
  const adminView = `${API}/manuals/administrator?key=${k}`;
  const adminDl = `${API}/manuals/administrator?key=${k}&download=true`;

  const DownloadIcon = ({ href, label }) => (
    <a
      href={href}
      onClick={(e) => e.stopPropagation()}
      aria-label={label}
      title={label}
      className="absolute top-3 right-3 w-9 h-9 rounded-lg flex items-center justify-center bg-black/40 hover:bg-[#A3E635] hover:text-black text-[#A3E635] transition-colors"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    </a>
  );

  return (
    <div className="card-glass p-6 mb-8" data-testid="admin-manuals-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#A3E635]/10 flex items-center justify-center">
          <svg className="w-5 h-5 text-[#A3E635]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
          </svg>
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold text-white">Manuals (v1.0)</h2>
          <p className="text-[#E8E4DC]/60 text-xs">Click any box to view in a new tab. Use the icon in the corner to download.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Subscriber Manual — entire box is a hyperlink */}
        <a
          href={subView}
          target="_blank"
          rel="noopener noreferrer"
          className="relative block rounded-xl border border-white/10 p-5 bg-white/5 hover:bg-white/10 hover:border-[#A3E635]/40 transition-all group"
          data-testid="admin-subscriber-manual"
        >
          <DownloadIcon href={subDl} label="Download Subscriber's Manual" />
          <span className="inline-block text-[10px] font-mono px-2 py-0.5 rounded bg-[#A3E635]/15 text-[#A3E635] mb-2">
            PUBLIC · v1.0
          </span>
          <h3 className="font-semibold text-white text-base mb-1 group-hover:text-[#A3E635] transition-colors">
            Subscriber's Manual
          </h3>
          <p className="text-[#E8E4DC]/70 text-xs mb-3">
            16 chapters · Every feature, setting, and shortcut · indexed
          </p>
          <span className="text-[#A3E635] text-xs inline-flex items-center gap-1">
            Open PDF
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </a>

        {/* Administrator Manual — entire box is a hyperlink */}
        <a
          href={adminView}
          target="_blank"
          rel="noopener noreferrer"
          className="relative block rounded-xl border border-red-500/20 p-5 bg-red-500/5 hover:bg-red-500/10 hover:border-red-400/40 transition-all group"
          data-testid="admin-administrator-manual"
        >
          <DownloadIcon href={adminDl} label="Download Administrator's Manual" />
          <span className="inline-block text-[10px] font-mono px-2 py-0.5 rounded bg-red-500/15 text-red-400 mb-2">
            ADMIN · v1.0
          </span>
          <h3 className="font-semibold text-white text-base mb-1 group-hover:text-red-300 transition-colors">
            Administrator's Manual
          </h3>
          <p className="text-[#E8E4DC]/70 text-xs mb-3">
            28 chapters · Subscriber content + 12 admin-only chapters
          </p>
          <span className="text-red-300 text-xs inline-flex items-center gap-1">
            Open PDF
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </a>
      </div>

      <p className="text-[#E8E4DC]/50 text-[11px] mt-3">
        The public Subscriber's Manual is also accessible without authentication at <code className="text-[#A3E635]">topscreen.org/manual</code>.
      </p>
    </div>
  );
}
